import {
  CenterBaseline,
  Icon,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
} from '@customerio/pluma-components/react';
import { STATIC_VALUE_SOURCE } from '../../codecs/flat-comparison-filter';
import { showsValueSource, valueSourceChangePatch } from './value-source';
import type { ConditionControlSize, OperatorValuePatch, ValueOption } from './types';

type ValueSourceSelectProps = {
  /** The row's operator. The select shows only while it can compare. */
  operator: string;
  valueSource: string | undefined;
  /** The surface's sources. Absent means the surface offers no select. */
  options: ValueOption[] | undefined;
  /** The operators the select shows for, where the surface narrows them. Absent means every operator that can compare. */
  comparableOperators?: readonly string[];
  disabled: boolean | undefined;
  onChange: (patch: OperatorValuePatch) => void;
  size: ConditionControlSize;
};

/**
 * What the condition's value is compared against: the literal typed in the
 * value field, or an attribute read when the condition is evaluated.
 *
 * The help icon is there because people expect to set the value's type here,
 * as they do in segments, and there is nothing to set.
 */
export function ValueSourceSelect({
  operator,
  valueSource,
  options,
  comparableOperators,
  disabled,
  onChange,
  size,
}: ValueSourceSelectProps) {
  if (options == null || !showsValueSource(operator, options, comparableOperators)) {
    return null;
  }
  return (
    <>
      <Select
        data-testid="filter-value-source-select"
        ariaLabel="Comparison operator"
        size={size}
        options={options}
        value={valueSource ?? STATIC_VALUE_SOURCE}
        isDisabled={disabled}
        onChange={(next: string | null) => {
          const patch = valueSourceChangePatch(valueSource, next);
          if (patch != null) {
            onChange(patch);
          }
        }}
      />
      <CenterBaseline isInline>
        <Popover shouldTriggerOnHover initialFocus={-1}>
          <PopoverTrigger data-testid="filter-value-source-help">
            <Icon name="help" size="sm" color="secondary" />
          </PopoverTrigger>
          <PopoverContent data-testid="filter-value-source-help-content">
            Choose what to compare against. Comparing to the current person's attribute reads the value from their
            profile when this condition is evaluated, so no value-type setup is needed.
          </PopoverContent>
        </Popover>
      </CenterBaseline>
    </>
  );
}
